<template>
  <div class="container booking-request px-0 pt-5 my-5">
    <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(submit)">
      <h2 class="text-center mb-3">{{ $t("listing.bookingRequest") }}</h2>
      <div class="card my-4">
        <div class="card-body px-md-5 shadow-sm">
          <h5 class="mb-4">{{ $t("listing.bookingDetails") }}</h5>
          <div class="row custom-input">
            <div class="col-md-5 my-3">
              <b-form-input
                readonly
                class="check-in form-control no-border"
                type="text"
                name="check-in"
                autocomplete="off"
                :placeholder="`${$t('products.checkIn')}*`"
                v-model="formData.check_in"
                id="from"
              />
            </div>
            <div class="col-md-5 my-3">
              <b-form-input
                readonly
                class="check-out form-control no-border"
                type="text"
                name="check-out"
                autocomplete="off"
                :placeholder="`${$t('products.checkOut')}*`"
                v-model="formData.check_out"
                id="to"
              />
            </div>
            <div class="col-md-10 my-3">
              <ValidatedInput
                name="guests"
                input-name="guest-count"
                autocomplete="off"
                type="number"
                v-model="formData.guests"
                :placeholder="`${$t('listing.noOfGuest')}*`"
                rules="required"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="card my-4" ref="bookerDetails">
        <div class="card-body px-md-5 shadow-sm">
          <h5 class="mb-4">{{ $t("listing.yourDetails") }}</h5>
          <div class="row">
            <div class="col-md-5 my-3">
              <ValidatedInput
                name="first name"
                input-name="booker-first-name"
                autocomplete="given-name"
                v-model="formData.first_name"
                :placeholder="`${$t('listing.firstName')}*`"
                rules="required"
                @blur="formData.guest_first_name = formData.first_name"
              />
            </div>
            <div class="col-md-5 my-3">
              <ValidatedInput
                name="last name"
                input-name="booker-last-name"
                autocomplete="family-name"
                v-model="formData.last_name"
                :placeholder="`${$t('listing.lastName')}*`"
                rules="required"
                @blur="formData.guest_last_name = formData.last_name"
              />
            </div>
            <div class="col-md-5 my-3">
              <ValidatedInput
                name="email"
                type="email"
                input-name="booker-email"
                autocomplete="email"
                v-model="formData.email"
                :placeholder="`${$t('listing.email')}*`"
                rules="required|email"
              />
            </div>
            <div class="col-md-5 my-3 no-border">
              <ValidatedInput
                type="tel"
                name="phone"
                input-name="booker-phone"
                autocomplete="tel"
                v-model="formData.phone"
                :placeholder="$t('reservation.placeholderPhone')"
                rules="required"
              />
            </div>
            <div class="col-md-5 my-3">
              <ValidatedInput
                name="post code"
                input-name="booker-postal-code"
                autocomplete="postal-code"
                v-model="formData.post_code"
                :placeholder="`${$t('listing.postCode')}*`"
                rules="required"
              />
            </div>
            <div class="col-md-5 my-3">
              <ValidatedInput
                name="city"
                input-name="booker-city"
                autocomplete="address-level2"
                v-model="formData.city"
                :placeholder="`${$t('listing.city')}*`"
                rules="required"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="card my-4">
        <div class="card-body px-md-5 shadow-sm">
          <h5 class="mb-4">{{ $t("listing.guestDetails") }}</h5>
      <fieldset class="border-0 p-0 m-0">
          <div class="row">
            <div class="col-md-5 my-3">
              <ValidatedInput
                name="guest first name"
                input-name="guest-first-name"
                autocomplete="section-guest given-name"
                v-model="formData.guest_first_name"
                :placeholder="`${$t('listing.firstName')}*`"
                rules="required"
              />
            </div>
            <div class="col-md-5 my-3">
              <ValidatedInput
                name="guest last name"
                input-name="guest-last-name"
                autocomplete="section-guest family-name"
                v-model="formData.guest_last_name"
                :placeholder="`${$t('listing.lastName')}*`"
                rules="required"
              />
            </div>
          </div>
      </fieldset>
        </div>
      </div>

      <div class="card my-4">
        <div class="card-body px-md-5 shadow-sm">
          <div class="d-flex justify-content-between">
            <h5 class="mb-4">{{ $t("listing.companyDetails") }}</h5>
            <div>
              <SectionButton
                :value="showCompanyDetails"
                @clicked="showCompanyDetails = !showCompanyDetails"
              />
            </div>
          </div>
          <div class="row" v-if="showCompanyDetails">
            <div class="col-md-10 my-3">
              <ValidatedInput
                name="company name"
                input-name="company-name"
                autocomplete="section-company organization"
                v-model="formData.company_name"
                :placeholder="`${$t('listing.companyName')}`"
                rules=""
              />
            </div>
            <div class="col-md-10 my-3">
              <ValidatedInput
                name="company address 1"
                input-name="company-address-line1"
                autocomplete="section-company address-line1"
                v-model="formData.company_address_1"
                :placeholder="`${$t('listing.address1')}`"
                rules=""
              />
            </div>
            <div class="col-md-10 my-3">
              <ValidatedInput
                name="company address 2"
                input-name="company-address-line2"
                autocomplete="section-company address-line2"
                v-model="formData.company_address_2"
                :placeholder="`${$t('listing.address2')}`"
                rules=""
              />
            </div>
            <div class="col-md-3 my-3">
              <ValidatedInput
                name="company city"
                input-name="company-city"
                autocomplete="section-company address-level2"
                v-model="formData.company_city"
                :placeholder="`${$t('listing.city')}`"
                rules=""
              />
            </div>
            <div class="col-md-3 my-3">
              <ValidatedInput
                name="company post code"
                input-name="company-postal-code"
                autocomplete="section-company postal-code"
                v-model="formData.company_post_code"
                :placeholder="`${$t('listing.postCode')}`"
                rules=""
              />
            </div>
            <div class="col-md-4 my-3">
              <ValidatedInput
                type="country"
                name="company country"
                input-name="company-country"
                autocomplete="section-company country-name"
                v-model="formData.company_country"
                :placeholder="`${$t('listing.country')}`"
                rules=""
              />
            </div>
          </div>
        </div>
      </div>

      <div class="card my-4">
        <div class="card-body px-md-5 shadow-sm">
          <div class="d-flex justify-content-between">
            <h5 class="mb-4">{{ $t("listing.specialRequest") }}</h5>
            <div>
              <SectionButton
                :value="showSpecialRequest"
                @clicked="showSpecialRequest = !showSpecialRequest"
              />
            </div>
          </div>
          <div class="row" v-if="showSpecialRequest">
            <div class="col-md-10 my-3">
              <ValidatedInput
                name="special request"
                type="textarea"
                input-name="special-request"
                autocomplete="off"
                v-model="formData.special_request"
                :placeholder="`${$t('listing.specialRequestOrWish')}`"
                rules=""
              />
            </div>
          </div>
        </div>
      </div>

      <b-alert v-if="submitError" show variant="danger" class="mt-3">
        {{ submitError }}
      </b-alert>

      <div class="text-right">
        <PrimaryButton
          class="ml-auto mt-3"
          :loading="pending"
          :text="$t('listing.sendRequest')"
          type="submit"
        />
      </div>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import moment from "moment";
import { mapState, mapActions } from "vuex";
import DateRangePicker from "../../helpers/datepicker/index";
import SectionButton from "../SectionButton.vue";
import { stashBookingConfirmation } from "../../helpers/bookingConfirmation";

export default {
  name: "BookingRequestForm",
  components: {
    SectionButton,
  },
  props: {
    property: {
      type: Object,
      required: true,
    },
    propertyInfo: {
      type: Object,
      required: true,
    },
    showCalendar: {
      type: Boolean,
      default: true,
    },
    date: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      showCompanyDetails: false,
      showSpecialRequest: false,
      submitError: "",
      formData: {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        post_code: "",
        city: "",
        guest_first_name: "",
        guest_last_name: "",
        check_in: "",
        check_out: "",
        guests: "",
        company_name: "",
        company_address_1: "",
        company_address_2: "",
        company_city: "",
        company_post_code: "",
        company_country: "",
        special_request: "",
      },
    };
  },
  computed: {
    ...mapState("booking", ["pending"]),
  },
  mounted() {
    if (this.showCalendar) {
      this.initCalendar();
    } else {
      this.formData.check_in = this.date?.checkIn || moment(new Date()).format("YYYY-MM-DD");
      this.formData.check_out = this.date?.checkOut || moment(new Date()).add(1, "day").format("YYYY-MM-DD");
    }
  },
  methods: {
    ...mapActions("booking", ["initiateBookingOnRequest"]),
    initCalendar() {
      const checkIn = moment(new Date()).format("YYYY-MM-DD");
      const checkOut = moment(new Date()).add(1, "day").format("YYYY-MM-DD");
      this.formData.check_in = checkIn;
      this.formData.check_out = checkOut;
      new DateRangePicker("#from", "#to", {
        format: "yy-mm-dd",
        smartCalendar: false,
        defaultFromDate: moment(checkIn, "YYYY-MM-DD").toDate(),
        defaultToDate: moment(checkOut, "YYYY-MM-DD").toDate(),
      });
    },
    async submit() {
      this.submitError = "";

      const isValid = await this.$refs.observer.validate();
      if (!isValid) {
        this.$refs.bookerDetails.scrollIntoView({ behavior: "smooth" });
        return;
      }

      const language = this.propertyInfo.languages.find(
        ({ isMain }) => !!isMain
      );

      let email = this.property.emails.find(
        ({ emailType }) => emailType.code === "primary"
      );
      if (email) {
        email = email.email;
      } else {
        email = this.property.emails[0]?.email;
      }

      if (!email) {
        this.submitError = this.$t("reservation.reservationError");
        this.$toastr.e(this.submitError);
        return;
      }

      try {
        await this.initiateBookingOnRequest({
          ...this.formData,
          property_id: this.property.id,
          property_name: this.property.name,
          property_email: email,
          property_address: this.propertyInfo.addresses.length
            ? this.propertyInfo.addresses[0]?.addressLine
            : "N/A",
          property_phone: this.property.phones.length
            ? this.property.phones[0]?.phoneNumber
            : "N/A",
          property_language: language ? language.language?.code || "en" : "en",
        });
        stashBookingConfirmation({ email: this.formData.email });
        this.$router.push({
          path: "/reservation_success",
          query: {
            type: "C",
            propertyId: this.property.id,
          },
        });
      } catch (error) {
        this.submitError =
          error.message || this.$t("reservation.reservationError");
        this.$toastr.e(this.submitError);
      }
    },
  },
};
</script>

<style>
@import "../../assets/css/calendar.css";

.booking-request h2,
.booking-request .card-body h5 {
  color: var(--primary-color);
  font-weight: bold;
  text-transform: uppercase;
}
</style>
